import { useEffect, useState } from 'react';

/**
 * Installation de l'application sur l'écran d'accueil.
 *
 * <p>Chrome et Edge émettent `beforeinstallprompt` très tôt, souvent avant
 * que React ne soit monté : l'événement est retenu ici dès le chargement du
 * module, puis rejoué au bouton « Installer » quand l'utilisateur le touche.
 * Safari (iPhone, iPad) n'émet rien : on explique le geste à faire.
 */

let evenementInstallation = null;
const abonnes = new Set();

/** Retient l'événement d'installation (appelé au chargement de l'application). */
export function ecouteInstallation() {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    evenementInstallation = e;
    abonnes.forEach((f) => f(e));
  });
  window.addEventListener('appinstalled', () => {
    evenementInstallation = null;
    abonnes.forEach((f) => f(null));
  });
}

/**
 * L'application tourne-t-elle déjà installée (fenêtre autonome) ?
 *
 * @returns {boolean} vrai en mode autonome
 */
export function estInstallee() {
  if (typeof window === 'undefined') return false;
  // L'application de l'App Store ou de Google Play est déjà installée.
  if (window.Capacitor?.isNativePlatform?.()) return true;
  if (window.navigator && window.navigator.standalone) return true;
  return !!(window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
}

/**
 * Appareil Apple mobile, où l'installation passe par le menu Partager.
 *
 * @param {string} [agent] chaîne d'agent (par défaut celle du navigateur)
 * @returns {boolean} vrai sur iPhone, iPad ou iPod
 */
export function estIos(agent = typeof navigator === 'undefined' ? '' : navigator.userAgent) {
  return /iPhone|iPad|iPod/i.test(agent || '');
}

/**
 * Navigateur utilisé sur iPhone : le bouton Partager n'est pas au même
 * endroit dans Safari (en bas), dans Chrome (en haut à droite, dans la barre
 * d'adresse) ou dans Firefox (dans le menu ≡).
 *
 * @param {string} [agent] chaîne d'agent
 * @returns {'safari'|'chrome'|'firefox'|'edge'} le navigateur
 */
export function navigateurIos(agent = typeof navigator === 'undefined' ? '' : navigator.userAgent) {
  const a = agent || '';
  if (/CriOS/i.test(a)) return 'chrome';
  if (/FxiOS/i.test(a)) return 'firefox';
  if (/EdgiOS/i.test(a)) return 'edge';
  return 'safari';
}

/**
 * Navigateur intégré à une autre application (WhatsApp, Instagram, Gmail,
 * Facebook…) : on ne peut pas y ajouter une page à l'écran d'accueil, il faut
 * d'abord l'ouvrir dans Safari.
 *
 * @param {string} [agent] chaîne d'agent
 * @returns {boolean} vrai dans un navigateur intégré
 */
export function dansUneAutreApp(
  agent = typeof navigator === 'undefined' ? '' : navigator.userAgent,
) {
  return /FBAN|FBAV|Instagram|Line\/|WhatsApp|GSA\/|Snapchat|LinkedInApp|Twitter|MicroMessenger/i.test(
    agent || '',
  );
}

/**
 * État de l'installation, et la commande pour la lancer.
 *
 * @returns {{proposable: boolean, installee: boolean, ios: boolean, installer: () => Promise<boolean>}}
 */
export function useInstallation() {
  const [evenement, setEvenement] = useState(evenementInstallation);
  const [installee, setInstallee] = useState(estInstallee);
  useEffect(() => {
    const suit = (e) => {
      setEvenement(e);
      if (!e) setInstallee(estInstallee());
    };
    abonnes.add(suit);
    return () => abonnes.delete(suit);
  }, []);

  const installer = async () => {
    if (!evenement) return false;
    evenement.prompt();
    const choix = await evenement.userChoice.catch(() => null);
    const accepte = !!choix && choix.outcome === 'accepted';
    if (accepte) {
      evenementInstallation = null;
      setEvenement(null);
      setInstallee(true);
    }
    return accepte;
  };

  return { proposable: !!evenement && !installee, installee, ios: estIos(), installer };
}

/** Réservé aux tests : simule l'événement du navigateur. */
export function _simuleEvenementInstallation(e) {
  evenementInstallation = e;
  abonnes.forEach((f) => f(e));
}
